import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import { place } from "../lib/format";
import { Button } from "../ui/Button";
import { Spinner } from "./Spinner";

/* Pick a company by name, for a person's employer while editing.
 *
 * A box, a short list of matches from api.company as the person types, and
 * the chosen company shown with a way to change it. The search asks the
 * server after a pause, so a slow typist does not fire a request per key. */

export type PickedCompany = { id: string; name: string };

type Row = { id: string | null; name: string | null; hq_city: string | null; hq_state: string | null; property_count: number | null };

const safe = (q: string) => q.replace(/[,()*\\]/g, " ").trim();

export function CompanyPicker({ token, value, onChange, label = "Employer" }: {
  token: string;
  value: PickedCompany | null;
  onChange: (v: PickedCompany | null) => void;
  label?: string;
}) {
  const [q, setQ] = useState("");
  const [asked, setAsked] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setAsked(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const matches = useQuery({
    queryKey: ["company-pick", asked],
    enabled: asked.length >= 2,
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("company").select("id,name,hq_city,hq_state,property_count")
        .ilike("name", `*${safe(asked)}*`)
        .order("property_count", { ascending: false, nullsFirst: false })
        .limit(8);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Row[];
    },
  });

  if (value) {
    return (
      <span className="picker-chosen">
        {value.name}
        <Button kind="link" size="compact-xs" className="picker-change" onClick={() => { onChange(null); setQ(""); }}>
          Change
        </Button>
      </span>
    );
  }

  return (
    <div className="picker">
      <TextInput aria-label={label} value={q} w="16rem" autoFocus
                 onChange={(e) => setQ(e.currentTarget.value)} />
      {asked.length >= 2 ? (
        matches.isLoading ? <p className="muted picker-note"><Spinner />Looking.</p>
        : matches.isError ? <p className="refusal picker-note">{(matches.error as Error).message}</p>
        : !matches.data?.length ? <p className="muted picker-note">No company by that name. EVIE adds a company from a page or a file, not from here yet.</p>
        : (
          <ul className="picker-list">
            {matches.data.map((c) => c.id && c.name ? (
              <li key={c.id}>
                <button type="button" className="picker-item" onClick={() => onChange({ id: c.id as string, name: c.name as string })}>
                  <span className="picker-name">{c.name}</span>
                  <span className="muted"> {place(c.hq_city, c.hq_state)}{c.property_count ? ` · ${c.property_count.toLocaleString()} properties` : ""}</span>
                </button>
              </li>
            ) : null)}
          </ul>
        )
      ) : <p className="muted picker-note">Type at least two letters of the name.</p>}
    </div>
  );
}
